import { useState, useEffect, useCallback } from "react";
import { getDoc, type DocumentReference } from "firebase/firestore";

interface UseFirestoreDocOnceResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  /** Re-runs the fetch. Call after a mutation to pick up changes. */
  refetch: () => void;
}

/**
 * One-time Firestore document fetch (getDoc, not onSnapshot).
 *
 * Per ADR-012 and ADR-015: edit pages and single-user admin flows read once
 * on mount rather than subscribing, because real-time updates would conflict
 * with form state and the admin is single-editor. Call `refetch` after a
 * mutation to pick up the latest values.
 */
export function useFirestoreDocOnce<T>(
  docRef: DocumentReference | null,
): UseFirestoreDocOnceResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Version counter — incremented by refetch() to force the effect to re-run
  // without relying on docRef object identity changing.
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!docRef) {
      setData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getDoc(docRef)
      .then((snapshot) => {
        if (cancelled) return;
        if (snapshot.exists()) {
          setData({ id: snapshot.id, ...snapshot.data() } as T);
        } else {
          setData(null);
        }
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [docRef, version]);

  return { data, loading, error, refetch };
}
